import { getDefaultTime24, to12HourFormat } from "./time";

export type ScheduleType = "daily" | "weekly" | "monthly" | "annually";

export type DeconstructedCronExpression = {
  scheduleType: ScheduleType;
  timeDisplay: string;
  time24?: string;
  daysOfWeek?: string[];
  daysOfMonth?: string[];
  month?: string;
};

/**
 * Generate a cron expression from a DeconstructedCronExpression
 * @param data - The DeconstructedCronExpression to generate a cron expression from
 * @returns The cron expression
 */
export function generateCronExpression(data: DeconstructedCronExpression) {
  const [hour, minute] = data.time24?.split(":") || ["0", "0"];

  switch (data.scheduleType) {
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekly":
      const daysOfWeek = data.daysOfWeek?.join(",") || "0";
      return `${minute} ${hour} * * ${daysOfWeek}`;
    case "monthly":
      return `${minute} ${hour} ${data.daysOfMonth} * *`;
    case "annually":
      return `${minute} ${hour} ${data.daysOfMonth} ${data.month} *`;
    default:
      return "0 0 * * *";
  }
}

/**
 * Parse a cron expression into a DeconstructedCronExpression
 * @param cronExp - The cron expression to parse
 * @returns The DeconstructedCronExpression
 */
export function parseCronExpression(
  cronExp?: string,
): DeconstructedCronExpression {
  const defaultTime = getDefaultTime24();
  const defaultTimeDisplay = to12HourFormat(defaultTime);

  if (!cronExp) {
    return {
      scheduleType: "daily",
      time24: defaultTime,
      timeDisplay: defaultTimeDisplay,
    };
  }

  const [minute, hour, daysOfMonth, month, daysOfWeek] = cronExp.split(" ");

  const time24 = `${hour?.padStart(2, "0")}:${minute?.padStart(2, "0")}`;

  // Helper function to expand day ranges (e.g., "1-5" to ["1", "2", "3", "4", "5"])
  const expandDayRanges = (dayString: string): string[] => {
    const parts = dayString.split(",");
    const expandedDays: string[] = [];

    for (const part of parts) {
      if (part.includes("-")) {
        const [start, end] = part.split("-").map(Number);
        for (let i = start; i <= end; i++) {
          expandedDays.push(String(i));
        }
      } else {
        expandedDays.push(part);
      }
    }

    return expandedDays;
  };

  if (daysOfMonth === "*" && month === "*" && daysOfWeek !== "*") {
    return {
      scheduleType: "weekly",
      time24: time24,
      timeDisplay: to12HourFormat(time24),
      daysOfWeek: expandDayRanges(daysOfWeek),
    };
  }

  if (daysOfMonth === "*" && month === "*" && daysOfWeek === "*") {
    return {
      scheduleType: "daily",
      time24: time24,
      timeDisplay: to12HourFormat(time24),
    };
  }

  if (daysOfMonth !== "*" && month === "*" && daysOfWeek === "*") {
    return {
      scheduleType: "monthly",
      time24: time24,
      timeDisplay: to12HourFormat(time24),
      daysOfMonth: daysOfMonth?.split(","),
    };
  }

  if (daysOfMonth !== "*" && month !== "*" && daysOfWeek === "*") {
    return {
      scheduleType: "annually",
      time24: time24,
      timeDisplay: to12HourFormat(time24),
      daysOfMonth: daysOfMonth?.split(","),
      month,
    };
  }

  // Return default values if parsing fails
  return {
    scheduleType: "daily",
    time24: defaultTime,
    timeDisplay: defaultTimeDisplay,
  };
}
